import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AssetUrlPipe } from '../../../core/pipes/asset-url.pipe';

export interface AgentFilterState {
  types: string[];
  statuses: string[];
}

@Component({
  selector: 'app-agent-filter-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, AssetUrlPipe],
  templateUrl: './agent-filter-modal.component.html',
  styleUrl: './agent-filter-modal.component.scss',
})
export class AgentFilterModalComponent implements OnInit, AfterViewInit, OnDestroy {
  activeModal = inject(NgbActiveModal);
  private zone = inject(NgZone);

  @Input() activeFilters: AgentFilterState = { types: [], statuses: [] };

  @ViewChild('contentPanel') contentPanel!: ElementRef<HTMLElement>;
  @ViewChildren('filterSection') filterSections!: QueryList<ElementRef<HTMLElement>>;

  isActive = false;
  activeSectionIndex = 0;

  readonly sidebarItems = [
    { key: 'engineType', label: 'Engine Type' },
    { key: 'status',     label: 'Status' },
  ];

  readonly typeOptions = [
    { value: 'Local',   iconSrc: 'images/virtusa-icons/local-agent.svg' },
    { value: 'Cluster', iconSrc: 'images/virtusa-icons/cluster-agent.svg' },
  ];

  readonly statusOptions = [
    { value: 'Online',    icon: 'check-circle' },
    { value: 'Offline',   icon: 'dash-circle' },
    { value: 'Executing', icon: 'arrow-repeat' },
    { value: 'Crashed',   icon: 'exclamation-triangle' },
  ];

  selectedType: string = '';
  selectedStatuses: string[] = [];

  private scrollListener!: () => void;

  ngOnInit(): void {
    this.selectedType     = this.activeFilters.types?.[0] ?? '';
    this.selectedStatuses = [...(this.activeFilters.statuses ?? [])];
  }

  ngAfterViewInit(): void {
    const el = this.contentPanel.nativeElement;
    this.zone.runOutsideAngular(() => {
      this.scrollListener = () => this.onScroll();
      el.addEventListener('scroll', this.scrollListener, { passive: true });
    });
  }

  ngOnDestroy(): void {
    this.contentPanel?.nativeElement.removeEventListener('scroll', this.scrollListener);
  }

  onScroll(): void {
    const container = this.contentPanel.nativeElement;
    const sections  = this.filterSections.toArray();
    let activeIdx   = 0;

    for (let i = 0; i < sections.length; i++) {
      const top = sections[i].nativeElement.offsetTop - container.scrollTop;
      if (top <= 24) {
        activeIdx = i;
      }
    }
    this.zone.run(() => (this.activeSectionIndex = activeIdx));
  }

  scrollToSection(index: number): void {
    const sections = this.filterSections.toArray();
    const target   = sections[index]?.nativeElement;
    if (target) {
      this.contentPanel.nativeElement.scrollTo({ top: target.offsetTop, behavior: 'smooth' });
    }
    this.activeSectionIndex = index;
  }

  isStatusSelected(value: string): boolean  { return this.selectedStatuses.includes(value); }

  toggleStatus(value: string): void {
    this.selectedStatuses = this.selectedStatuses.includes(value)
      ? this.selectedStatuses.filter(s => s !== value)
      : [...this.selectedStatuses, value];
  }

  get hasActiveFilters(): boolean {
    return !!this.selectedType || this.selectedStatuses.length > 0;
  }

  reset(): void {
    this.selectedType     = '';
    this.selectedStatuses = [];
  }

  apply(): void {
    this.activeModal.close({
      types:    this.selectedType ? [this.selectedType] : [],
      statuses: this.selectedStatuses,
    } as AgentFilterState);
  }

  dismiss(): void {
    this.activeModal.dismiss();
  }
}
